var _a;
import * as React from 'react';
import { subscribe } from '@bounded-sh/core';
// SSR guard (mirrors useAuth): no hooks/subscriptions during Node server render.
// React Native has no `window` but DOES support hooks, so detect Node SSR
// specifically via process.versions.node.
const isSSR = typeof window === 'undefined'
    && typeof process !== 'undefined'
    && !!((_a = process.versions) === null || _a === void 0 ? void 0 : _a.node);
export function useQuery(path, options) {
    if (isSSR) {
        return { data: undefined, loading: true, error: null };
    }
    const [data, setData] = React.useState(undefined);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    // Re-subscribe only when the query actually changes (not on every render).
    const optionsKey = options ? JSON.stringify(options) : '';
    React.useEffect(() => {
        if (!path) {
            setData(undefined);
            setLoading(false);
            setError(null);
            return;
        }
        let active = true;
        let unsubscribe;
        setLoading(true);
        setError(null);
        subscribe(path, Object.assign(Object.assign({}, options), { onData: (d) => {
                if (!active)
                    return;
                // A reconnect can legitimately surface a transport error before the
                // socket re-authenticates and sends a new authorized snapshot. That
                // fresh snapshot is the recovery boundary: keep the error visible
                // until data arrives, then clear it so React does not remain stuck on
                // a failure page after realtime has recovered.
                setError(null);
                setData(d);
                setLoading(false);
            }, onError: (e) => {
                if (!active)
                    return;
                setError(e instanceof Error ? e : new Error(String(e)));
                setLoading(false);
            } }))
            .then((unsub) => {
            if (active)
                unsubscribe = unsub;
            else
                void unsub(); // unmounted before subscribe resolved
        })
            .catch((e) => {
            if (!active)
                return;
            setError(e instanceof Error ? e : new Error(String(e)));
            setLoading(false);
        });
        return () => {
            active = false;
            void (unsubscribe === null || unsubscribe === void 0 ? void 0 : unsubscribe());
        };
        // optionsKey captures the option object's contents; path is the other input.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [path, optionsKey]);
    return { data, loading, error };
}
